// Pure derivation behind the fleet grid — no React, no GraphQL, so it stays testable.

import { CEILINGS, SERVICE_TYPES, VEHICLE_TYPES } from '../constants';
import { equipmentKeys } from '../utils';
import { recommendedEquipmentLabels } from '@/constants/recommendedEquipmentLabels';
import { genName } from '@/components/Subregions/utils';

const VEHICLE_LABELS = Object.fromEntries(VEHICLE_TYPES.map(({ value, label }) => [value, label]));
const SERVICE_LABELS = Object.fromEntries(SERVICE_TYPES.map(({ value, label }) => [value, label]));

export const vehicleLabel = (resource) => VEHICLE_LABELS[resource?.vehicle_type] || '';

// Labels, not keys: the grid is read, not parsed. Unknown keys fall through as themselves rather
// than vanishing, so a value the UI does not know about is still visible.
export const equipmentLabels = (resource) =>
    equipmentKeys(resource?.equipment).map((key) => recommendedEquipmentLabels[key] || key);

export const serviceTypeLabels = (resource) =>
    (resource?.service_types || []).map((value) => SERVICE_LABELS[value] || value);

export const subregionCount = (resource) => (resource?.subregions || []).length;

// Built from the ordered list rather than read off each subregion, because genName falls back to
// "Subregion N" on an unnamed one and N is its position — the same label the resource dialog shows.
export const subregionLabelsById = (subregionsOrdered = []) =>
    Object.fromEntries(subregionsOrdered.map((subregion, idx) => [subregion.subregion_id, genName(subregion, idx)]));

export const subregionNames = (resource, labelsById = {}) =>
    (resource?.subregions || []).map(({ subregion_id }) => labelsById[subregion_id]).filter(Boolean);

// Any non-null ceiling is set, a zero included. Shipper availability reads only cubes and weight,
// and turns a booking away when its total is strictly over the ceiling — so a zero there hides
// every day from a booking that carries cubes or weight. It is a broken ceiling, not a missing one;
// the grid marks it as such.
export const configuredCeilings = (resource) => CEILINGS.filter(({ column }) => resource?.[column] != null);

export const blocksEveryBooking = (limit) => limit != null && Number(limit) <= 0;

// What a ceiling at or below 0 does, for the grid's tooltip. Nothing refuses a booking: only shipper
// availability reads a ceiling, and only cubes and weight. The Plan page treats 0 as no ceiling.
const AVAILABILITY_CEILINGS = ['cubes', 'weight'];

export const zeroCeilingHint = (value) =>
    AVAILABILITY_CEILINGS.includes(value)
        ? 'Hides every day from shipper availability. The Plan page treats it as no ceiling. Set a value above 0.'
        : 'Treated as no ceiling: shipper availability does not read it, and the Plan page ignores it. Set a value above 0.';

export const hasNoCeilings = (resource) => configuredCeilings(resource).length === 0;

// Every one the truck backfill created has no subregion. Shipper booking (genResources, and the
// availability API's zone filter) matches on the resource's subregion zips, so shippers cannot book
// these; the Plan page does not filter on zones, so they still run routes there. A valid setup.
export const hasNoSubregion = (resource) => subregionCount(resource) === 0;

export const NOT_SHIPPER_BOOKABLE = 'Not shipper-bookable';

export const NOT_SHIPPER_BOOKABLE_HINT =
    'No subregion, so shippers cannot book this resource through availability. It can still run routes on the Plan page.';

export const fleetStats = (resources = []) => ({
    total: resources.length,
    noCeilings: resources.filter(hasNoCeilings).length,
    noSubregion: resources.filter(hasNoSubregion).length,
});

// Name, vehicle and subregion are what an operator scans for. Ceilings are numbers nobody searches by.
export const filterResources = (resources = [], search = '', labelsById = {}) => {
    const term = search.trim().toLowerCase();

    if (!term) {
        return resources;
    }

    return resources.filter((resource) =>
        [resource?.name, vehicleLabel(resource), ...subregionNames(resource, labelsById)]
            .filter(Boolean)
            .some((field) => String(field).toLowerCase().includes(term))
    );
};
